import { CliError, CliErrorCode } from '@wix/cli-error';
import { pathToFileURL } from 'node:url';
import { createPluginsInterface } from './plugins/plugins-interface.js';
import {
  DYNAMIC_WIX_CONFIG_FILENAME,
  getExistingWixConfigPaths,
  validateWixConfig,
} from './wix-config.js';
import type { DefinedWixConfig } from './types/wix-astro-config.js';

function validateDefinedWixConfig(
  value: unknown,
  configPath: string
): DefinedWixConfig {
  if (typeof value !== 'object' || value == null || Array.isArray(value)) {
    throw new CliError({
      code: CliErrorCode.InvalidConfigDefinition({
        reason: '`defineWixConfig` expects a configuration object.',
      }),
      cause: null,
    });
  }

  if (!Object.hasOwn(value, 'config')) {
    throw new CliError({
      code: CliErrorCode.InvalidConfigDefinition({
        reason: '`defineWixConfig` expects a `config` property.',
      }),
      cause: null,
    });
  }

  return {
    config: validateWixConfig(
      (value as { config: unknown }).config,
      configPath
    ),
    pluginsInterface: createPluginsInterface(
      (value as { plugins?: unknown }).plugins
    ),
  };
}

export const importWixConfig = async (
  projectFolder: string
): Promise<DefinedWixConfig> => {
  const { dynamicConfigPath, legacyConfigPath } =
    await getExistingWixConfigPaths(projectFolder);

  const allConfigPaths = [
    ...(dynamicConfigPath != null ? [dynamicConfigPath] : []),
    ...(legacyConfigPath != null ? [legacyConfigPath] : []),
  ];

  if (allConfigPaths.length > 1) {
    throw new CliError({
      code: CliErrorCode.MultipleConfigFiles({
        paths: allConfigPaths,
      }),
      cause: null,
    });
  }

  if (dynamicConfigPath == null) {
    throw new CliError({
      code: CliErrorCode.InvalidConfigDefinition({
        reason: `This project expected to have dynamic wix config in "${DYNAMIC_WIX_CONFIG_FILENAME}"`,
      }),
      cause: null,
    });
  }

  let importedConfig: Record<PropertyKey, unknown>;
  const pathToConfig = pathToFileURL(dynamicConfigPath).href;
  try {
    importedConfig = await import(pathToConfig);
  } catch (error) {
    throw new CliError({
      code: CliErrorCode.InvalidConfigDefinition({
        reason: `Failed to import wix config from "${pathToConfig}"`,
      }),
      cause: error,
    });
  }

  if (!Object.hasOwn(importedConfig, 'default')) {
    throw new CliError({
      code: CliErrorCode.ConfigNoDefaultExport({
        path: dynamicConfigPath,
      }),
      cause: null,
    });
  }

  return validateDefinedWixConfig(importedConfig.default, dynamicConfigPath);
};
